/**
 * CountLink — a count in a table cell or card that opens the list it counts (OP#1854).
 *
 * Rule: every alert / offline number in the UI is a link to a filtered list that explains it.
 * Zero recedes to a muted dash and is NOT a link (there is nothing to open). A non-zero count
 * keeps its status tone (the status ramp is operational state, so it stays) and gains a
 * hover/focus underline plus a visually-hidden description for screen readers.
 *
 * Rows and cards around it are themselves clickable (DataGrid onRowActivate, CardList's
 * role="button" card), so click AND keydown are stopped here: without that, Enter on the link
 * would also fire the card's Enter handler and navigate twice.
 */
import type { KeyboardEvent, MouseEvent, ReactNode } from "react";
import { Link } from "react-router-dom";
import { cn } from "@/lib/cn";

export interface CountLinkProps {
  n: number;
  to: string;
  tone: "warn" | "critical";
  /** Screen-reader description of the destination, e.g. "open alerts for CyberTan". */
  label: string;
  className?: string;
  /** Optional visible content instead of the bare number (e.g. "3 critical"). */
  children?: ReactNode;
}

export function CountLink({ n, to, tone, label, className, children }: CountLinkProps) {
  if (n === 0) return <span className="text-ink-subtle">—</span>;
  const stop = (e: MouseEvent | KeyboardEvent) => e.stopPropagation();
  return (
    <Link
      to={to}
      onClick={stop}
      onKeyDown={stop}
      title={label}
      className={cn(
        "inline-flex min-h-[1.5rem] items-center rounded font-medium tabular-nums",
        "underline decoration-transparent underline-offset-2 transition-colors",
        "hover:decoration-current focus-visible:decoration-current",
        tone === "critical" ? "text-critical" : "text-warn",
        className,
      )}
    >
      {children ?? n}
      <span className="sr-only"> — {label}</span>
    </Link>
  );
}
